import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export interface MapMarking {
  id: number;
  geom: { type: "Point"; coordinates: [number, number] };
  type: "VAURIO" | "ESTE" | "KORJAUSEHDOTUS" | "MUU";
  description: string;
  status: "OPEN" | "ACKNOWLEDGED" | "RESOLVED";
  photo?: { id: string; filename: string } | null;
  creatorUser?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
  };
  resolvedAt: string | null;
  createdAt: string;
}

interface MapMarkingsState {
  items: MapMarking[];
  status: "idle" | "loading" | "failed";
}

const initialState: MapMarkingsState = { items: [], status: "idle" };

export const fetchMapMarkings = createAsyncThunk<MapMarking[], number>(
  "mapMarkings/fetch",
  async (associationId) => {
    const res = await axiosInstance.get<MapMarking[]>(
      `/associations/${associationId}/map-markings`
    );
    return res.data;
  }
);

/** Marking photos go through their own endpoint (gated on mapMarking:create)
 *  so members without general file-create rights can attach photos. */
export const uploadMarkingPhoto = createAsyncThunk<
  { id: string; filename: string },
  { file: File; associationId: number }
>("mapMarkings/uploadPhoto", async ({ file, associationId }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("associationId", String(associationId));
  const res = await axiosInstance.post("/map-markings/upload-photo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
});

export const createMapMarking = createAsyncThunk<
  MapMarking,
  {
    associationId: number;
    lon: number;
    lat: number;
    type: string;
    description: string;
    photoFileId?: string | null;
  }
>("mapMarkings/create", async ({ associationId, ...body }) => {
  const res = await axiosInstance.post<MapMarking>(
    `/associations/${associationId}/map-markings`,
    body
  );
  return res.data;
});

export const updateMapMarkingStatus = createAsyncThunk<
  MapMarking,
  { id: number; status: string }
>("mapMarkings/updateStatus", async ({ id, status }) => {
  const res = await axiosInstance.put<MapMarking>(
    `/map-markings/${id}/status`,
    { status }
  );
  return res.data;
});

export const deleteMapMarking = createAsyncThunk<{ id: number }, number>(
  "mapMarkings/delete",
  async (id) => {
    await axiosInstance.delete(`/map-markings/${id}`);
    return { id };
  }
);

const slice = createSlice({
  name: "mapMarkings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMapMarkings.pending, (s) => {
        s.status = "loading";
      })
      .addCase(
        fetchMapMarkings.fulfilled,
        (s, a: PayloadAction<MapMarking[]>) => {
          s.items = a.payload;
          s.status = "idle";
        }
      )
      .addCase(fetchMapMarkings.rejected, (s) => {
        s.status = "failed";
      })
      .addCase(createMapMarking.fulfilled, (s, a) => {
        s.items = [a.payload, ...s.items];
      })
      .addCase(updateMapMarkingStatus.fulfilled, (s, a) => {
        const idx = s.items.findIndex((m) => m.id === a.payload.id);
        if (idx !== -1) s.items[idx] = { ...s.items[idx], ...a.payload };
      })
      .addCase(deleteMapMarking.fulfilled, (s, a) => {
        s.items = s.items.filter((m) => m.id !== a.payload.id);
      });
  },
});

export default slice.reducer;
